import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, MessageSquare, Layers, BookOpen, Factory, Mail, Home, Info, AtSign, PanelBottom, LogOut, Menu, X } from "lucide-react";
import { useState } from "react";
import { useAdminAuth } from "@/hooks/useAdminAuth";

const NAV = [
  { to: "/ngxp-admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/ngxp-admin/contacts", label: "Contacts", icon: MessageSquare },
  { to: "/ngxp-admin/pages/home", label: "Home Page", icon: Home },
  { to: "/ngxp-admin/pages/about", label: "About Page", icon: Info },
  { to: "/ngxp-admin/pages/contact", label: "Contact Page", icon: AtSign },
  { to: "/ngxp-admin/pages/footer", label: "Footer", icon: PanelBottom },
  { to: "/ngxp-admin/services", label: "Services", icon: Layers },
  { to: "/ngxp-admin/blogs", label: "Blogs", icon: BookOpen },
  { to: "/ngxp-admin/industries", label: "Industries", icon: Factory },
  { to: "/ngxp-admin/smtp", label: "SMTP", icon: Mail },
];

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/ngxp-admin");
  };

  return (
    <div className="min-h-screen bg-[#0A0B10] flex">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#0D0E13] border-r border-white/5 flex flex-col transition-transform duration-300 ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      >
        <div className="p-6 border-b border-white/5 flex items-center gap-2">
          <span className="font-display text-xl font-bold gradient-text-glow">NGXP</span>
          <span className="text-xs text-white/30 uppercase tracking-widest">Admin</span>
        </div>

        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to;
            return (
              <Link
                key={to}
                to={to}
                onClick={() => setOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  active
                    ? "bg-[#7B2CBF]/20 text-white border border-[#7B2CBF]/30"
                    : "text-gray-400 hover:text-white hover:bg-white/5"
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </Link>
            );
          })}
        </nav>

        <div className="p-4 border-t border-white/5">
          <div className="px-4 py-2 mb-2">
            <p className="text-xs text-gray-600">Signed in as</p>
            <p className="text-sm text-white font-medium">{admin?.username ?? "Admin"}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-red-400/10 transition-all"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
        {/* Mobile header */}
        <header className="sticky top-0 z-30 bg-[#0A0B10]/90 backdrop-blur border-b border-white/5 px-5 py-4 flex items-center justify-between lg:hidden">
          <span className="font-display text-lg font-bold gradient-text-glow">NGXP Admin</span>
          <button onClick={() => setOpen(!open)} className="text-white p-1">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </header>

        <main className="flex-1 p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
}
